import { CalenderFillerIcon } from '@/components/base/Icons/RocheIconPack';
import { useIsAggregatedConsentPages } from '@/components/consent/useIsAggregatedConsentPages';
import { useIsPartnerScorePages } from '@/components/partner';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { useAvoidBackgroundScrolling } from '@/hooks';
import { FilterType } from '@/models';
import ActionChip from '@/storybook/components/ActionChip/ActionChip';
import Button from '@/storybook/components/Button/Button';
import Drawer from '@/storybook/components/Drawer/Drawer';
import Text from '@/storybook/components/Text/Text';
import { formatPeriodShortWithYear } from '@/utils/date';
import cx from 'classnames';
import React, { useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { TestIds } from '../testids';
import { DateRangeInput } from '@/storybook/components/DateRangeInput/DateRangeInput';
import styles from './SelectedDateRange.module.css';
import { SelectedDateRangeProps } from './utils/types/SelectedDateRangeProps';
import usePreviousDateRangeText from './utils/usePreviousDateRangeText';
import { useDateRangeDrawer } from './utils/useDateRangeDrawer';
import { AppRoutes } from '@/utils/constants';
import { translateDynamicKey } from '@/types/translation';
import { matchAppRoute } from '@/utils/matchAppRoute';
import { TFuncKey } from 'i18next';
import { useLocation } from 'react-router-dom';

const titleMap: Partial<Record<AppRoutes, TFuncKey>> = {
  [AppRoutes.INSIGHTS_KPIS]: 'header.navigation.dateRange.insightsHeading',
  [AppRoutes.INSIGHTS_ANALYSIS]: 'header.navigation.dateRange.insightsHeading',
};

const SelectedDateRange: React.FC<SelectedDateRangeProps> = ({ dateRangePeriod, previousDateRangePeriod }) => {
  const { t } = useTranslation();
  const isPartnerScorePages = useIsPartnerScorePages();
  const isAggregatedConsentPages = useIsAggregatedConsentPages();
  const dateRange = formatPeriodShortWithYear(dateRangePeriod);
  const previousDateRangeText = usePreviousDateRangeText(previousDateRangePeriod);
  const calendarIconRef = useRef<HTMLButtonElement>(null);
  const { filterOverlayVisible } = useFiltersContext();
  const { pathname } = useLocation();
  const currentRoute = useMemo(() => matchAppRoute(pathname), [pathname]);
  const titleKey = currentRoute ? titleMap[currentRoute] : undefined;
  const title = translateDynamicKey(t, titleKey ?? 'header.navigation.dateRange.heading');

  const {
    open,
    hasPendingChanges,
    formRef,
    methods,
    quickSelectOptions,
    selectedOptionId,
    isDirty,
    hasValidDateRanges,
    toggle,
    handleClose,
    handleCancel,
    handleApplyClick,
    handleQuickSelect,
    handleClearAll,
    onSubmit,
  } = useDateRangeDrawer();

  useAvoidBackgroundScrolling(filterOverlayVisible);

  if (isPartnerScorePages || isAggregatedConsentPages) {
    return null;
  }

  return (
    <div className={cx(styles.dateRangeContainer)} data-testid={TestIds.topBar.datepicker}>
      <Button onClick={toggle} variant="text" ref={calendarIconRef} className={styles.calenderBtn}>
        <div className={styles.calenderWrapper}>
          <CalenderFillerIcon className={cx(styles.calenderIcon)} data-testid="calendar-icon" />
        </div>
        <div className={styles.dateRangeLabel}>
          <Text as="h5" className={styles.dateRange}>
            {dateRange}
          </Text>
          {previousDateRangeText && (
            <Text as="span" className={styles.subDateRange}>
              {previousDateRangeText}
            </Text>
          )}
        </div>
      </Button>

      <Drawer
        isOpen={open}
        onClose={handleClose}
        title={title}
        position="top"
        contentClassName={styles.drawerContent}
        data-testid="date-picker-drawer"
        showCloseButton={true}
        disablePortal
        footerContent={
          <div className={styles.buttonContainer}>
            <Button variant="text" onClick={handleClearAll} data-testid="clear-all-button">
              {t('header.navigation.dateRange.clearAllButton')}
            </Button>
            <div>
              <Button onClick={handleCancel} variant="text">
                {t('header.navigation.dateRange.cancelButtonText')}
              </Button>
              <Button
                onClick={handleApplyClick}
                disabled={!hasValidDateRanges || (!isDirty && !hasPendingChanges)}
                variant={hasPendingChanges ? 'pending' : 'primary'}
                data-testid="apply-date-button"
                {...(hasPendingChanges && {
                  'data-tooltip-id': 'apply-button-tooltip',
                  'data-tooltip-content': t('header.navigation.dateRange.applyTooltip'),
                })}
              >
                {t('header.navigation.dateRange.buttonText')}
              </Button>
              {hasPendingChanges && <Tooltip id="apply-button-tooltip" place="top-start" />}
            </div>
          </div>
        }
      >
        <>
          <section className={styles.quickSelectSection}>
            <Text as="h4" className={styles.sectionHeading}>
              Quick select
            </Text>
            <div className={styles.quickSelectGrid}>
              {quickSelectOptions.map(option => (
                <ActionChip
                  key={option.id}
                  title={option.title}
                  primaryText={option.primaryText}
                  secondaryText={option.secondaryText}
                  isSelected={selectedOptionId === option.id}
                  onClick={() => handleQuickSelect(option)}
                  data-testid={`quick-select-${option.id}`}
                />
              ))}
            </div>
          </section>
          <FormProvider {...methods}>
            <form id="date-range-form" ref={formRef} onSubmit={onSubmit}>
              <Text as="h4" className={styles.sectionHeading}>
                Manual selection
              </Text>
              <div className={styles.filterBodyComparisonPeriodContainer}>
                <div className={styles.dateRangeInputWrapper}>
                  <DateRangeInput name={FilterType.DATE_RANGE} label="Date range" />
                </div>
                <div className={styles.dateRangeInputWrapper}>
                  <DateRangeInput name={FilterType.COMPARISON_PERIOD} label="Comparison period" />
                </div>
              </div>
            </form>
          </FormProvider>
        </>
      </Drawer>
    </div>
  );
};

export default SelectedDateRange;
